import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { ImageUpload } from './ImageUpload';
import type { OutfitCategory } from '@/types';
import { useOutfitStore } from '@/stores/outfitStore';

interface OutfitCardProps {
  title: string;
  category: OutfitCategory;
  icon: React.ReactNode;
  outfitId: number;
  images: string[];
  notes?: string;
  maxImages?: number;
  isOpen?: boolean;
}

export const OutfitCard: React.FC<OutfitCardProps> = ({
  title,
  category,
  icon,
  outfitId,
  images,
  notes = '',
  maxImages = 4,
  isOpen = false,
}) => {
  const { t } = useTranslation();
  const { updateOutfit } = useOutfitStore();
  const [expanded, setExpanded] = useState(isOpen);
  const [localNotes, setLocalNotes] = useState(notes);
  const [localImages, setLocalImages] = useState(images);

  const handleImagesChange = (newImages: string[]) => {
    setLocalImages(newImages);
  };

  const handleNotesBlur = async () => {
    if (localNotes !== notes) {
      await updateOutfit(outfitId, { [category]: localNotes });
    }
  };

  React.useEffect(() => {
    setLocalImages(images);
    setLocalNotes(notes);
  }, [images, notes]);

  const filled = localImages.length;
  const hasContent = filled > 0 || !!localNotes;

  return (
    <div className="bg-white rounded-2xl border border-border shadow-[0_2px_12px_rgba(194,24,91,0.05)] overflow-hidden transition-all duration-300 hover:shadow-[0_8px_24px_rgba(194,24,91,0.10)] hover:-translate-y-0.5">
      {/* Header — tap to expand */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/30 transition-colors focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none rounded-t-2xl"
        aria-expanded={expanded}
        aria-controls={`card-body-${category}`}
      >
        <div className="flex items-center gap-4">
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors duration-300 ${
              hasContent
                ? 'bg-gradient-to-br from-[#E91E8C] to-primary text-white shadow-md shadow-primary/20'
                : 'bg-secondary text-primary'
            }`}
            aria-hidden="true"
          >
            {icon}
          </div>
          <div className="text-left">
            <h3 className="font-serif italic text-base font-bold text-foreground leading-tight">
              {title}
            </h3>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-0.5">
              {filled > 0
                ? `${filled}/${maxImages} ${t('common.photos')}`
                : t('outfit.addPhotos')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasContent && (
            <Sparkles className="w-3.5 h-3.5 text-primary/60" aria-hidden="true" />
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </button>

      {/* Expandable body */}
      {expanded && (
        <div
          id={`card-body-${category}`}
          className="px-5 pb-5 pt-3 border-t border-border animate-spring-in"
        >
          <div className="space-y-4">
            <ImageUpload
              category={category}
              existingImages={localImages}
              maxImages={maxImages}
              outfitId={outfitId}
              onImagesChange={handleImagesChange}
            />
            <textarea
              value={localNotes}
              onChange={e => setLocalNotes(e.target.value)}
              onBlur={handleNotesBlur}
              placeholder={`${t('outfit.notes')}...`}
              rows={2}
              className="w-full px-4 py-3 text-sm bg-background border border-border rounded-xl resize-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all font-sans text-foreground placeholder:text-muted-foreground/60 outline-none"
              aria-label={`Note per ${title}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
